// Beam and Profile Calculator
// Comprehensive calculations for PMMA rods, tubes, and profiles

import { logModuleInit } from '@/utils/invariant';
logModuleInit('BeamCalculator.ts');

import { MATERIALS, MaterialProperties } from './MaterialDatabase';

export interface BeamProfile {
  type: 'rod' | 'tube' | 'rectangular' | 'square_tube' | 'L_profile' | 'T_profile' | 'U_profile';
  name: string;
  dimensions: {
    diameter?: number; // dla prętów
    outerDiameter?: number; // dla rur
    innerDiameter?: number; // dla rur
    width?: number; // dla profili prostokątnych
    height?: number; // dla profili prostokątnych
    wallThickness?: number; // dla profili zamkniętych
    flangeWidth?: number; // dla profili L, T, U
    flangeThickness?: number; // dla profili L, T, U
    webHeight?: number; // dla profili T, U
    webThickness?: number; // dla profili T, U
  };
  material: string;
}

export interface BeamLoad {
  type: 'point' | 'uniform' | 'moment' | 'triangular';
  value: number; // N lub N/m
  position?: number; // mm od początku (dla obciążenia punktowego)
  startPos?: number; // mm (dla obciążenia rozłożonego)
  endPos?: number; // mm (dla obciążenia rozłożonego)
  direction: 'vertical' | 'horizontal' | 'axial';
}

export interface BeamSupport {
  type: 'fixed' | 'pinned' | 'roller' | 'free';
  position: number; // mm od początku
  allowedRotation: boolean;
  allowedTranslationX: boolean;
  allowedTranslationY: boolean;
}

export interface BeamCalculationInput {
  profile: BeamProfile;
  length: number; // mm
  supports: BeamSupport[];
  loads: BeamLoad[];
  temperature?: number; // °C
  considerBuckling: boolean;
  considerTorsion: boolean;
  considerDynamicLoads: boolean;
  dynamicFactor?: number; // współczynnik dynamiczny
  safetyFactor: number;
}

export interface BeamCalculationResult {
  // Podstawowe wyniki
  maxDeflection: number; // mm
  maxDeflectionPosition: number; // mm od początku
  maxBendingStress: number; // MPa
  maxShearStress: number; // MPa
  maxTorsionalStress?: number; // MPa
  vonMisesStress: number; // MPa (naprężenie zastępcze)
  
  // Reakcje podporowe
  reactions: Array<{
    position: number;
    forceX: number;
    forceY: number;
    moment?: number;
  }>;
  
  // Diagramy
  bendingMomentDiagram: Array<{ x: number; M: number }>;
  shearForceDiagram: Array<{ x: number; V: number }>;
  deflectionCurve: Array<{ x: number; y: number }>;
  
  // Stateczność
  bucklingLoad?: number; // N (siła krytyczna wyboczenia)
  bucklingFactor?: number; // współczynnik bezpieczeństwa na wyboczenie
  lateralBucklingMoment?: number; // Nm (moment krytyczny zwichrzenia)
  
  // Częstotliwości drgań własnych
  naturalFrequencies?: number[]; // Hz
  
  // Status i rekomendacje
  isSafe: boolean;
  criticalMode: 'bending' | 'shear' | 'buckling' | 'torsion' | 'deflection';
  utilizationRatio: number; // % wykorzystania nośności
  recommendations: string[];
  
  // Dane do wizualizacji
  stressDistribution: Array<{ x: number; y: number; stress: number }>;
  criticalSections: Array<{
    position: number;
    type: string;
    value: number;
    severity: 'low' | 'medium' | 'high' | 'critical';
  }>;
}

export class BeamCalculator {
  
  /**
   * Główna funkcja obliczeniowa dla belek
   */
  static calculate(input: BeamCalculationInput): BeamCalculationResult {
    // Pobierz właściwości materiału
    const material = MATERIALS[input.profile.material];
    if (!material) {
      throw new Error(`Nieznany materiał: ${input.profile.material}`);
    }
    
    // Oblicz właściwości geometryczne przekroju
    const sectionProperties = this.calculateSectionProperties(input.profile);
    
    // Oblicz reakcje podporowe
    const reactions = this.calculateReactions(input);
    
    // Generuj diagramy sił wewnętrznych
    const bendingMomentDiagram = this.generateBendingMomentDiagram(input, reactions);
    const shearForceDiagram = this.generateShearForceDiagram(input, reactions);
    
    // Znajdź wartości maksymalne
    const maxMoment = Math.max(...bendingMomentDiagram.map(p => Math.abs(p.M)));
    const maxShear = Math.max(...shearForceDiagram.map(p => Math.abs(p.V)));
    
    // Oblicz naprężenia
    const maxBendingStress = (maxMoment * sectionProperties.yMax) / sectionProperties.Ix / 1e6; // MPa
    const maxShearStress = (maxShear * sectionProperties.Q) / (sectionProperties.Ix * sectionProperties.t) / 1e6; // MPa
    
    // Oblicz ugięcie
    const deflectionCurve = this.calculateDeflection(
      input,
      bendingMomentDiagram,
      material.modulusOfElasticity * 1e6,
      sectionProperties.Ix
    );
    const maxDeflection = Math.max(...deflectionCurve.map(p => Math.abs(p.y)));
    const maxDeflectionPosition = deflectionCurve.find(p => Math.abs(p.y) === maxDeflection)?.x || 0;
    
    // Sprawdź wyboczenie jeśli wymagane
    let bucklingLoad: number | undefined;
    let bucklingFactor: number | undefined;
    
    if (input.considerBuckling) {
      bucklingLoad = this.calculateBucklingLoad(input, sectionProperties, material);
      const maxAxialLoad = this.getMaxAxialLoad(input.loads);
      bucklingFactor = bucklingLoad / maxAxialLoad;
    }
    
    // Oblicz naprężenie zastępcze von Misesa
    const vonMisesStress = Math.sqrt(
      Math.pow(maxBendingStress, 2) + 3 * Math.pow(maxShearStress, 2)
    );
    
    // Określ tryb krytyczny
    const criticalMode = this.determineCriticalMode(
      maxBendingStress,
      maxShearStress,
      maxDeflection,
      bucklingFactor,
      input,
      material
    );
    
    // Oblicz wykorzystanie nośności
    const utilizationRatio = (vonMisesStress / material.flexuralStrength) * 100;
    
    // Sprawdź bezpieczeństwo
    const isSafe = utilizationRatio < (100 / input.safetyFactor) &&
                   (!bucklingFactor || bucklingFactor > input.safetyFactor) &&
                   maxDeflection < input.length / 250;
    
    // Generuj rekomendacje
    const recommendations = this.generateRecommendations(
      input,
      maxDeflection,
      maxBendingStress,
      bucklingFactor,
      material,
      sectionProperties
    );
    
    // Znajdź przekroje krytyczne
    const criticalSections = this.findCriticalSections(
      bendingMomentDiagram,
      shearForceDiagram,
      deflectionCurve,
      material
    );
    
    // Oblicz rozkład naprężeń
    const stressDistribution = this.calculateStressDistribution(
      bendingMomentDiagram,
      sectionProperties,
      input.length
    );
    
    return {
      maxDeflection,
      maxDeflectionPosition,
      maxBendingStress,
      maxShearStress,
      vonMisesStress,
      reactions,
      bendingMomentDiagram,
      shearForceDiagram,
      deflectionCurve,
      bucklingLoad,
      bucklingFactor,
      isSafe,
      criticalMode,
      utilizationRatio,
      recommendations,
      stressDistribution,
      criticalSections
    };
  }
  
  /**
   * Oblicz właściwości geometryczne przekroju
   */
  static calculateSectionProperties(profile: BeamProfile): any {
    const props: any = {};
    
    switch (profile.type) {
      case 'rod':
        // Pręt okrągły
        const d = profile.dimensions.diameter!;
        props.A = Math.PI * Math.pow(d, 2) / 4; // mm²
        props.Ix = Math.PI * Math.pow(d, 4) / 64; // mm⁴
        props.Iy = props.Ix;
        props.Wx = Math.PI * Math.pow(d, 3) / 32; // mm³
        props.Wy = props.Wx;
        props.yMax = d / 2;
        props.t = d; // grubość dla ścinania
        props.Q = (Math.PI * Math.pow(d, 2) / 8) * (2 * d / (3 * Math.PI)); // moment statyczny
        props.J = Math.PI * Math.pow(d, 4) / 32; // moment bezwładności na skręcanie
        break;
        
      case 'tube':
        // Rura
        const Do = profile.dimensions.outerDiameter!;
        const Di = profile.dimensions.innerDiameter!;
        props.A = Math.PI * (Math.pow(Do, 2) - Math.pow(Di, 2)) / 4;
        props.Ix = Math.PI * (Math.pow(Do, 4) - Math.pow(Di, 4)) / 64;
        props.Iy = props.Ix;
        props.Wx = props.Ix / (Do / 2);
        props.Wy = props.Wx;
        props.yMax = Do / 2;
        props.t = Do - Di;
        props.Q = (Math.PI * (Math.pow(Do, 2) - Math.pow(Di, 2)) / 8) * (2 * Do / (3 * Math.PI));
        props.J = Math.PI * (Math.pow(Do, 4) - Math.pow(Di, 4)) / 32;
        break;
        
      case 'rectangular':
        // Profil prostokątny pełny
        const b = profile.dimensions.width!;
        const h = profile.dimensions.height!;
        props.A = b * h;
        props.Ix = (b * Math.pow(h, 3)) / 12;
        props.Iy = (h * Math.pow(b, 3)) / 12;
        props.Wx = props.Ix / (h / 2);
        props.Wy = props.Iy / (b / 2);
        props.yMax = h / 2;
        props.t = b;
        props.Q = (b * h / 2) * (h / 4);
        props.J = this.calculateTorsionConstant(profile);
        break;
        
      case 'square_tube':
        // Profil prostokątny zamknięty
        const bo = profile.dimensions.width!;
        const ho = profile.dimensions.height!;
        const tw = profile.dimensions.wallThickness!;
        const bi = bo - 2 * tw;
        const hi = ho - 2 * tw;
        props.A = bo * ho - bi * hi;
        props.Ix = (bo * Math.pow(ho, 3) - bi * Math.pow(hi, 3)) / 12;
        props.Iy = (ho * Math.pow(bo, 3) - hi * Math.pow(bi, 3)) / 12;
        props.Wx = props.Ix / (ho / 2);
        props.Wy = props.Iy / (bo / 2);
        props.yMax = ho / 2;
        props.t = 2 * tw;
        props.Q = 2 * tw * (ho - tw) * (ho / 2 - tw / 2);
        props.J = (2 * Math.pow(bi * hi, 2)) / (bi / tw + hi / tw);
        break;
        
      case 'L_profile':
        // Kątownik
        const lw = profile.dimensions.flangeWidth!;
        const lt = profile.dimensions.flangeThickness!;
        // Uproszczone obliczenia dla kątownika równoramiennego
        props.A = 2 * lw * lt - Math.pow(lt, 2);
        props.Ix = (2 * lt * Math.pow(lw, 3)) / 12;
        props.Iy = props.Ix;
        props.yMax = lw / Math.sqrt(2);
        props.t = lt;
        props.Q = lt * lw * lw / 4;
        props.J = (2 * Math.pow(lt, 3) * lw) / 3;
        break;
        
      default:
        throw new Error(`Nieobsługiwany typ profilu: ${profile.type}`);
    }
    
    return props;
  }
  
  /**
   * Oblicz reakcje podporowe
   */
  static calculateReactions(input: BeamCalculationInput): any[] {
    // Uproszczona metoda dla belek statycznie wyznaczalnych
    const reactions: any[] = [];
    
    // Dla uproszczenia - belka na dwóch podporach
    if (input.supports.length === 2) {
      const L = input.length;
      const support1 = input.supports[0];
      const support2 = input.supports[1];
      
      let totalLoad = 0;
      let totalMoment = 0; // względem pierwszej podpory
      
      // Sumuj obciążenia
      input.loads.forEach(load => {
        if (load.type === 'point' && load.direction === 'vertical') {
          totalLoad += load.value;
          totalMoment += load.value * (load.position || 0);
        } else if (load.type === 'uniform' && load.direction === 'vertical') {
          const loadLength = (load.endPos || L) - (load.startPos || 0);
          const totalUniformLoad = load.value * loadLength;
          totalLoad += totalUniformLoad;
          totalMoment += totalUniformLoad * ((load.startPos || 0) + loadLength / 2);
        }
      });
      
      // Oblicz reakcje
      const R2 = totalMoment / (support2.position - support1.position);
      const R1 = totalLoad - R2;
      
      reactions.push(
        {
          position: support1.position,
          forceX: 0,
          forceY: R1,
          moment: support1.type === 'fixed' ? 0 : undefined
        },
        {
          position: support2.position,
          forceX: 0,
          forceY: R2,
          moment: support2.type === 'fixed' ? 0 : undefined
        }
      );
    }
    
    return reactions;
  }
  
  /**
   * Generuj diagram momentów gnących
   */
  static generateBendingMomentDiagram(
    input: BeamCalculationInput,
    reactions: any[]
  ): Array<{ x: number; M: number }> {
    const points: Array<{ x: number; M: number }> = [];
    const steps = 100;
    
    for (let i = 0; i <= steps; i++) {
      const x = (i / steps) * input.length;
      let M = 0;
      
      // Dodaj wpływ reakcji
      reactions.forEach(reaction => {
        if (reaction.position < x) {
          M += reaction.forceY * (x - reaction.position);
        }
      });
      
      // Dodaj wpływ obciążeń
      input.loads.forEach(load => {
        if (load.type === 'point' && load.position! < x) {
          M -= load.value * (x - load.position!);
        } else if (load.type === 'uniform') {
          const start = load.startPos || 0;
          const end = Math.min(load.endPos || input.length, x);
          if (start < x) {
            const loadLength = end - start;
            M -= load.value * loadLength * (x - start - loadLength / 2);
          }
        }
      });
      
      points.push({ x, M });
    }
    
    return points;
  }
  
  /**
   * Generuj diagram sił tnących
   */
  static generateShearForceDiagram(
    input: BeamCalculationInput,
    reactions: any[]
  ): Array<{ x: number; V: number }> {
    const points: Array<{ x: number; V: number }> = [];
    const steps = 100;
    
    for (let i = 0; i <= steps; i++) {
      const x = (i / steps) * input.length;
      let V = 0;
      
      // Dodaj wpływ reakcji
      reactions.forEach(reaction => {
        if (reaction.position <= x) {
          V += reaction.forceY;
        }
      });
      
      // Dodaj wpływ obciążeń
      input.loads.forEach(load => {
        if (load.type === 'point' && load.position! <= x) {
          V -= load.value;
        } else if (load.type === 'uniform') {
          const start = load.startPos || 0;
          const end = Math.min(load.endPos || input.length, x);
          if (start < x) {
            V -= load.value * (end - start);
          }
        }
      });
      
      points.push({ x, V });
    }
    
    return points;
  }
  
  /**
   * Oblicz linię ugięcia
   */
  static calculateDeflection(
    input: BeamCalculationInput,
    momentDiagram: Array<{ x: number; M: number }>,
    E: number,
    I: number
  ): Array<{ x: number; y: number }> {
    const points: Array<{ x: number; y: number }> = [];
    
    // Metoda całkowania numerycznego (uproszczona)
    const dx = input.length / 100;
    let slope = 0;
    let deflection = 0;
    
    for (let i = 0; i < momentDiagram.length; i++) {
      const M = momentDiagram[i].M;
      const curvature = M / (E * I);
      
      slope += curvature * dx;
      deflection += slope * dx;
      
      points.push({
        x: momentDiagram[i].x,
        y: deflection * 1000 // konwersja na mm
      });
    }
    
    // Korekta na podporach (powinny być zerowe)
    if (input.supports.length === 2) {
      const support1Y = points[Math.floor(input.supports[0].position / dx)]?.y || 0;
      const support2Y = points[Math.floor(input.supports[1].position / dx)]?.y || 0;
      
      // Liniowa korekta
      points.forEach(p => {
        const ratio = p.x / input.length;
        p.y -= support1Y * (1 - ratio) + support2Y * ratio;
      });
    }
    
    return points;
  }
  
  /**
   * Oblicz siłę krytyczną wyboczenia
   */
  static calculateBucklingLoad(
    input: BeamCalculationInput,
    sectionProps: any,
    material: MaterialProperties
  ): number {
    // Wzór Eulera
    const E = material.modulusOfElasticity * 1e6; // Pa
    const I = Math.min(sectionProps.Ix, sectionProps.Iy); // mm⁴
    
    // Współczynnik długości wyboczeniowej
    let k = 1; // domyślnie dla przegubów na obu końcach
    if (input.supports.every(s => s.type === 'fixed')) {
      k = 0.5; // obustronne utwierdzenie
    } else if (input.supports.some(s => s.type === 'fixed')) {
      k = 0.7; // jednostronne utwierdzenie
    }
    
    const Leff = k * input.length; // mm
    
    const Pcr = (Math.pow(Math.PI, 2) * E * I) / Math.pow(Leff, 2);
    
    return Pcr;
  }
  
  /**
   * Znajdź maksymalne obciążenie osiowe
   */
  static getMaxAxialLoad(loads: BeamLoad[]): number {
    return Math.max(...loads
      .filter(l => l.direction === 'axial')
      .map(l => l.value), 0);
  }
  
  /**
   * Określ tryb krytyczny
   */
  static determineCriticalMode(
    bendingStress: number,
    shearStress: number,
    deflection: number,
    bucklingFactor: number | undefined,
    input: BeamCalculationInput,
    material: MaterialProperties
  ): 'bending' | 'shear' | 'buckling' | 'torsion' | 'deflection' {
    const maxDeflection = input.length / 250;
    
    if (bucklingFactor && bucklingFactor < 2) {
      return 'buckling';
    }
    
    if (deflection > maxDeflection) {
      return 'deflection';
    }
    
    if (shearStress > material.shearStrength * 0.5) {
      return 'shear';
    }
    
    return 'bending';
  }
  
  /**
   * Generuj rekomendacje
   */
  static generateRecommendations(
    input: BeamCalculationInput,
    deflection: number,
    stress: number,
    bucklingFactor: number | undefined,
    material: MaterialProperties,
    sectionProps: any
  ): string[] {
    const recommendations: string[] = [];
    
    // Sprawdź ugięcie
    const maxAllowedDeflection = input.length / 250;
    if (deflection > maxAllowedDeflection) {
      recommendations.push(
        `⚠️ Ugięcie ${deflection.toFixed(1)}mm przekracza dopuszczalne L/250 = ${maxAllowedDeflection.toFixed(1)}mm`
      );
      
      // Sugestie
      if (input.profile.type === 'rod') {
        const newDiameter = Math.ceil(input.profile.dimensions.diameter! * 1.2);
        recommendations.push(`💡 Zwiększ średnicę pręta do ${newDiameter}mm`);
      } else if (input.profile.type === 'tube') {
        recommendations.push(`💡 Zwiększ średnicę zewnętrzną lub zmniejsz wewnętrzną`);
      }
    }
    
    // Sprawdź naprężenia
    if (stress > material.flexuralStrength * 0.8) {
      recommendations.push(
        `⚠️ Naprężenia ${stress.toFixed(1)}MPa bliskie granicy wytrzymałości ${material.flexuralStrength}MPa`
      );
      recommendations.push(`💡 Użyj mocniejszego materiału lub zwiększ przekrój`);
    }
    
    // Sprawdź wyboczenie
    if (bucklingFactor && bucklingFactor < 3) {
      recommendations.push(
        `⚠️ Niski współczynnik bezpieczeństwa na wyboczenie: ${bucklingFactor.toFixed(1)}`
      );
      recommendations.push(`💡 Skróć długość belki lub dodaj podpory pośrednie`);
      recommendations.push(`💡 Zwiększ moment bezwładności przekroju`);
    }
    
    // Sugestie optymalizacyjne
    if (stress < material.flexuralStrength * 0.3 && deflection < maxAllowedDeflection * 0.5) {
      recommendations.push(`✅ Konstrukcja przewymiarowana - możliwa optymalizacja`);
      
      if (input.profile.type === 'rod') {
        const optimalDiameter = Math.floor(input.profile.dimensions.diameter! * 0.8);
        recommendations.push(`💡 Można zmniejszyć średnicę do ${optimalDiameter}mm`);
      }
    }
    
    return recommendations;
  }
  
  /**
   * Znajdź przekroje krytyczne
   */
  static findCriticalSections(
    momentDiagram: Array<{ x: number; M: number }>,
    shearDiagram: Array<{ x: number; V: number }>,
    deflectionCurve: Array<{ x: number; y: number }>,
    material: MaterialProperties
  ): any[] {
    const sections: any[] = [];
    
    // Znajdź maksymalny moment
    const maxMoment = momentDiagram.reduce((max, p) => 
      Math.abs(p.M) > Math.abs(max.M) ? p : max
    );
    
    sections.push({
      position: maxMoment.x,
      type: 'Maksymalny moment gnący',
      value: maxMoment.M,
      severity: Math.abs(maxMoment.M) > 1000 ? 'high' : 'medium'
    });
    
    // Znajdź maksymalną siłę tnącą
    const maxShear = shearDiagram.reduce((max, p) => 
      Math.abs(p.V) > Math.abs(max.V) ? p : max
    );
    
    sections.push({
      position: maxShear.x,
      type: 'Maksymalna siła tnąca',
      value: maxShear.V,
      severity: Math.abs(maxShear.V) > 500 ? 'high' : 'medium'
    });
    
    // Znajdź maksymalne ugięcie
    const maxDeflection = deflectionCurve.reduce((max, p) => 
      Math.abs(p.y) > Math.abs(max.y) ? p : max
    );
    
    sections.push({
      position: maxDeflection.x,
      type: 'Maksymalne ugięcie',
      value: maxDeflection.y,
      severity: Math.abs(maxDeflection.y) > 10 ? 'high' : Math.abs(maxDeflection.y) > 5 ? 'medium' : 'low'
    });
    
    return sections;
  }
  
  /**
   * Oblicz rozkład naprężeń
   */
  static calculateStressDistribution(
    momentDiagram: Array<{ x: number; M: number }>,
    sectionProps: any,
    length: number
  ): Array<{ x: number; y: number; stress: number }> {
    const distribution: Array<{ x: number; y: number; stress: number }> = [];
    
    // Generuj siatkę punktów
    const xSteps = 20;
    const ySteps = 10;
    
    for (let i = 0; i <= xSteps; i++) {
      const x = (i / xSteps) * length;
      const M = momentDiagram.find(p => Math.abs(p.x - x) < length / 100)?.M || 0;
      
      for (let j = 0; j <= ySteps; j++) {
        const y = ((j / ySteps) - 0.5) * 2 * sectionProps.yMax;
        const stress = (M * y) / sectionProps.Ix / 1e6; // MPa
        
        distribution.push({ x, y, stress });
      }
    }
    
    return distribution;
  }
  
  /**
   * Oblicz stałą skręcania dla przekrojów nieokrągłych
   */
  static calculateTorsionConstant(profile: BeamProfile): number {
    if (profile.type === 'rectangular') {
      const b = Math.min(profile.dimensions.width!, profile.dimensions.height!);
      const h = Math.max(profile.dimensions.width!, profile.dimensions.height!);
      const ratio = h / b;
      
      // Wzór przybliżony dla prostokąta
      let beta = 0.333;
      if (ratio < 2) beta = 0.333 - 0.21 * (b / h) * (1 - Math.pow(b / h, 4) / 12);
      
      return beta * b * Math.pow(h, 3);
    }
    
    // Dla innych przekrojów zwróć wartość przybliżoną
    return 0;
  }
}

// Przykładowe profile standardowe z PMMA
export const STANDARD_PROFILES: BeamProfile[] = [
  // Pręty okrągłe
  { type: 'rod', name: 'Pręt ø5mm', dimensions: { diameter: 5 }, material: 'pmma_xt' },
  { type: 'rod', name: 'Pręt ø8mm', dimensions: { diameter: 8 }, material: 'pmma_xt' },
  { type: 'rod', name: 'Pręt ø10mm', dimensions: { diameter: 10 }, material: 'pmma_xt' },
  { type: 'rod', name: 'Pręt ø12mm', dimensions: { diameter: 12 }, material: 'pmma_xt' },
  { type: 'rod', name: 'Pręt ø15mm', dimensions: { diameter: 15 }, material: 'pmma_xt' },
  { type: 'rod', name: 'Pręt ø20mm', dimensions: { diameter: 20 }, material: 'pmma_xt' },
  { type: 'rod', name: 'Pręt ø25mm', dimensions: { diameter: 25 }, material: 'pmma_xt' },
  { type: 'rod', name: 'Pręt ø30mm', dimensions: { diameter: 30 }, material: 'pmma_xt' },
  
  // Rury
  { type: 'tube', name: 'Rura ø10/6mm', dimensions: { outerDiameter: 10, innerDiameter: 6 }, material: 'pmma_xt' },
  { type: 'tube', name: 'Rura ø12/8mm', dimensions: { outerDiameter: 12, innerDiameter: 8 }, material: 'pmma_xt' },
  { type: 'tube', name: 'Rura ø15/10mm', dimensions: { outerDiameter: 15, innerDiameter: 10 }, material: 'pmma_xt' },
  { type: 'tube', name: 'Rura ø20/14mm', dimensions: { outerDiameter: 20, innerDiameter: 14 }, material: 'pmma_xt' },
  { type: 'tube', name: 'Rura ø25/19mm', dimensions: { outerDiameter: 25, innerDiameter: 19 }, material: 'pmma_xt' },
  { type: 'tube', name: 'Rura ø30/24mm', dimensions: { outerDiameter: 30, innerDiameter: 24 }, material: 'pmma_xt' },
  
  // Profile prostokątne
  { type: 'rectangular', name: 'Profil 10x20mm', dimensions: { width: 10, height: 20 }, material: 'pmma_xt' },
  { type: 'rectangular', name: 'Profil 15x30mm', dimensions: { width: 15, height: 30 }, material: 'pmma_xt' },
  { type: 'rectangular', name: 'Profil 20x40mm', dimensions: { width: 20, height: 40 }, material: 'pmma_xt' },
  { type: 'rectangular', name: 'Profil 25x50mm', dimensions: { width: 25, height: 50 }, material: 'pmma_xt' },
  { type: 'rectangular', name: 'Profil 30x60mm', dimensions: { width: 30, height: 60 }, material: 'pmma_xt' },
  
  // Profile kwadratowe zamknięte
  { type: 'square_tube', name: 'Rura kwadratowa 20x20x2mm', dimensions: { width: 20, height: 20, wallThickness: 2 }, material: 'pmma_xt' },
  { type: 'square_tube', name: 'Rura kwadratowa 30x30x3mm', dimensions: { width: 30, height: 30, wallThickness: 3 }, material: 'pmma_xt' },
  { type: 'square_tube', name: 'Rura kwadratowa 40x40x3mm', dimensions: { width: 40, height: 40, wallThickness: 3 }, material: 'pmma_xt' },
  { type: 'square_tube', name: 'Rura kwadratowa 50x50x4mm', dimensions: { width: 50, height: 50, wallThickness: 4 }, material: 'pmma_xt' }
];